<script setup lang="ts">
  import { computed, onMounted, onUnmounted, type PropType } from 'vue';
  import { useEntitySearch } from '../../lib';
  import { ColoredButton } from '@/shared/ui';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import { BRANCH_SORT_OPTIONS, type Branch } from '@/entities/Branch';
  import { useBranchManagement } from '@/pages/admin';
  import { BranchTable } from '@/pages/admin';
  import type { SortOrder } from '@/shared/lib';
  import { search } from '@/entities/Document';

  const props = defineProps({
    searchTerm: { type: String, default: '' },
    sort: { type: String, default: '' },
    order: { type: String, default: '' },
    filter: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
    context: { type: String as PropType<'search'>, default: 'search' },
    searchUrl: { type: String, default: '/branches/search' },
  });

  const {
    items: branches,
    metadata,
    loading,
    loadingMore,
    selectedQuantity,
    currentPage,
    isInfiniteScrollMode,
    searchTerm: activeSearchTerm,
    isUsingFilters,
    shouldShowNoResults,
    shouldShowNoFilterResults,
    handleSearch,
    handleSortOptions,
    handleQuantityChange,
    removeItems,
    onPageChanged,
    initSearch,
    cleanup,
  } = useEntitySearch<Branch>(
    { defaultSearchUrl: '/branches/search', searchFunction: search },
    {
      search: props.searchTerm,
      sort: { sort: props.sort, order: props.order as SortOrder },
      filter: props.filter,
      context: props.context,
      searchUrl: props.searchUrl,
      page: 1,
      amount: 10,
    }
  );

  const { navigateToCreate, deleteBranch } = useBranchManagement();

  const branchesList = computed<Branch[]>(() => {
    try {
      return branches.value ?? [];
    } catch (error) {
      console.error('Erro ao converter resultados para filiais:', error);
      return [];
    }
  });

  const handleDelete = async (id: number) => {
    const { success } = await deleteBranch(id);
    if (success) {
      removeItems([id]);
    }
  };

  onMounted(async () => {
    try {
      await initSearch();
    } catch (error) {
      console.error('Erro ao inicializar busca:', error);
    }
  });

  onUnmounted(() => {
    cleanup();
  });
</script>

<template>
  <div data-testid="admin-branches-page">
    <AdminListPageLayout
      title="Filiais"
      search-placeholder="Buscar filiais por nome..."
      entity-name="filial"
      empty-state-message="Nenhuma filial cadastrada"
      :items="branchesList"
      :loading="loading"
      :search-term="activeSearchTerm"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :is-using-filters="isUsingFilters"
      :current-page="currentPage"
      @search="handleSearch"
      @page-changed="onPageChanged"
      @update:selected-quantity="handleQuantityChange"
    >
      <template #action-buttons>
        <ColoredButton
          text="Criar Filial"
          :icon="PlusIcon"
          icon-alt="Ícone de adição"
          aria-label="Criar nova filial"
          @click="navigateToCreate"
        />
      </template>
      <template #table>
        <BranchTable
          :order-options="BRANCH_SORT_OPTIONS"
          :branches="branchesList"
          :loading="loading"
          :show-no-results="shouldShowNoResults || shouldShowNoFilterResults"
          @delete="handleDelete"
          @update:sort="(sort: string, order: string) => handleSortOptions(sort, order)"
        />
      </template>
    </AdminListPageLayout>

    <div
      v-if="isInfiniteScrollMode"
      id="infinite-scroll-trigger"
      class="py-4 text-center"
      role="status"
      :aria-live="loadingMore ? 'polite' : 'off'"
      :aria-busy="loadingMore"
    >
      <div
        v-if="loadingMore"
        class="py-2 text-gray-03"
      >
        Carregando mais filiais...
      </div>
    </div>
  </div>
</template>
